import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import api from "../lib/api";

// Custom storage that respects rememberMe setting
const customStorage = {
  getItem: (name) => {
    const rememberMe = localStorage.getItem("korvry-remember-me") === "true";
    const storage = rememberMe ? localStorage : sessionStorage;
    const str = storage.getItem(name);
    return str ? JSON.parse(str) : null;
  },
  setItem: (name, value) => {
    const rememberMe = localStorage.getItem("korvry-remember-me") === "true";
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem(name, JSON.stringify(value));
  },
  removeItem: (name) => {
    localStorage.removeItem(name);
    sessionStorage.removeItem(name);
  },
};

export const useAuthStore = create(
  persist(
    (set, get) => ({
      // ── State ──
      user: null,
      token: null,
      organizations: [],
      activeOrganization: null,
      isLoading: false,
      isInitialized: false,
      rememberMe: false,

      // ── Computed: Super Admin ──
      isSuperAdmin: () => {
        const { user } = get();
        return user?.isSuperAdmin === true;
      },

      // ── Setters ──
      setUser: (user) => set({ user }),
      setToken: (token) => set({ token }),
      setOrganizations: (orgs) => set({ organizations: orgs }),
      setActiveOrganization: (org) => set({ activeOrganization: org }),

      // ── Login ──
      login: async (email, password, rememberMe = false) => {
        set({ isLoading: true });
        try {
          const res = await api.post("/api/v2/auth/login", {
            email,
            password,
            rememberMe,
          });
          const {
            token,
            user,
            organizations,
            activeOrganizationId,
            expiresIn,
          } = res.data;

          const activeOrg =
            organizations?.find((o) => o.id === activeOrganizationId) ||
            organizations?.[0] ||
            null;

          if (rememberMe) {
            localStorage.setItem("korvry-remember-me", "true");
            localStorage.setItem("korvry-saved-email", email.trim().toLowerCase());
          } else {
            localStorage.removeItem("korvry-remember-me");
            localStorage.removeItem("korvry-saved-email");
          }

          set({
            token,
            user,
            organizations: organizations || [],
            activeOrganization: activeOrg,
            isLoading: false,
            isInitialized: true,
            rememberMe,
          });

          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          return {
            success: false,
            message: error.response?.data?.error || error.message,
          };
        }
      },

      // ── Register ──
      register: async (data) => {
        set({ isLoading: true });
        try {
          const payload = {
            contactName: data.contactName,
            email: data.email,
            password: data.password,
            phone: data.phone,
            country: data.country || "NG",
            organizationName: data.organizationName,
            industry: data.industry || "other",
          };

          const res = await api.post("/api/v2/auth/register", payload);
          const { token, user, organization } = res.data;

          const orgs = organization ? [organization] : [];
          const activeOrg = orgs[0] || null;

          localStorage.removeItem("korvry-remember-me");

          set({
            token,
            user,
            organizations: orgs,
            activeOrganization: activeOrg,
            isLoading: false,
            isInitialized: true,
            rememberMe: false,
          });

          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          return {
            success: false,
            message: error.response?.data?.error || error.message,
          };
        }
      },

      // ── Logout ──
      logout: () => {
        localStorage.removeItem("korvry-remember-me");
        sessionStorage.removeItem("auth-storage");
        localStorage.removeItem("auth-storage");
        set({
          user: null,
          token: null,
          organizations: [],
          activeOrganization: null,
          isLoading: false,
          isInitialized: false,
          rememberMe: Boolean(localStorage.getItem("korvry-saved-email")),
        });
      },

      // ── Switch Org ──
      switchOrganization: async (orgId) => {
        set({ isLoading: true });
        try {
          const res = await api.post("/api/v2/auth/switch-org", { orgId });
          const { token, role, organizationId } = res.data;

          const meRes = await api.get("/api/v2/auth/me");
          const { user, organizations } = meRes.data;

          const activeOrg =
            organizations?.find(
              (o) => o.id === organizationId || o._id === organizationId,
            ) ||
            organizations?.[0] ||
            null;

          set({
            token,
            user,
            organizations: organizations || [],
            activeOrganization: activeOrg,
            isLoading: false,
          });

          return { success: true, token };
        } catch (error) {
          set({ isLoading: false });
          return {
            success: false,
            message: error.response?.data?.error || error.message,
          };
        }
      },

      // ── Accept Invite ──
      acceptInvite: async ({ token, contactName, password, phone }) => {
        set({ isLoading: true });
        try {
          const payload = { token };
          if (contactName) payload.contactName = contactName;
          if (password) payload.password = password;
          if (phone) payload.phone = phone;

          const res = await api.post("/api/v2/auth/accept-invite", payload);
          const { token: jwt, user, organizationId, role } = res.data;

          const meRes = await api.get("/api/v2/auth/me");
          const { organizations } = meRes.data;
          const activeOrg = organizations?.find(
            (o) => o.id === organizationId,
          ) ||
            organizations?.[0] || {
              id: organizationId,
              name: "Unknown",
              role: role || "viewer",
            };

          localStorage.removeItem("korvry-remember-me");

          set({
            token: jwt,
            user,
            organizations: organizations || [activeOrg],
            activeOrganization: activeOrg,
            isLoading: false,
            isInitialized: true,
            rememberMe: false,
          });

          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          return {
            success: false,
            message: error.response?.data?.error || error.message,
          };
        }
      },

      // ── Create Organization ──
      createOrganization: async (data) => {
        set({ isLoading: true });
        try {
          const res = await api.post("/api/v2/auth/create-org", data);
          const { organization } = res.data;

          const meRes = await api.get("/api/v2/auth/me");
          const { user, organizations } = meRes.data;

          const activeOrg =
            organizations?.find(
              (o) => o.id === organization.id || o._id === organization.id,
            ) || organization;

          set({
            user,
            organizations: organizations || [],
            activeOrganization: activeOrg,
            isLoading: false,
          });

          return { success: true, organization };
        } catch (error) {
          set({ isLoading: false });
          return {
            success: false,
            message: error.response?.data?.error || error.message,
          };
        }
      },

      // ── Hydrate current user + orgs ──
      fetchMe: async () => {
        try {
          const res = await api.get("/api/v2/auth/me");
          const { user, organizations } = res.data;

          const currentActiveId =
            get().activeOrganization?.id || get().activeOrganization?._id;
          const activeOrg =
            organizations?.find(
              (o) => o.id === currentActiveId || o._id === currentActiveId,
            ) ||
            organizations?.[0] ||
            null;

          set({
            user,
            organizations,
            activeOrganization: activeOrg,
          });
          return { success: true };
        } catch (error) {
          return { success: false, message: error.message };
        }
      },

      // ── Helpers ──
      initializeAuth: () => {
        const { token, user, isInitialized } = get();
        if (token && user && !isInitialized) {
          set({ isInitialized: true });
        }
      },

      getRole: () => get().activeOrganization?.role || "viewer",

      can: (roles) => {
        const role = get().getRole();
        if (role === "owner") return true;
        const roleList = Array.isArray(roles) ? roles : [roles];
        if (roleList.includes(role)) return true;
        if (role === "admin" && (roleList.includes("manager") || roleList.includes("member") || roleList.includes("viewer"))) return true;
        if (role === "manager" && (roleList.includes("member") || roleList.includes("viewer"))) return true;
        return false;
      },
    }),
    {
      name: "korvry-auth",
      storage: customStorage,
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        organizations: state.organizations,
        activeOrganization: state.activeOrganization,
        rememberMe: state.rememberMe,
      }),
    },
  ),
);